/**
 * The category tree the header menu is built from.
 *
 * `GET /categories` returns a flat list and does no visibility filtering at
 * all — there is no `is_visible` column, and hiding a shelf is the convention
 * `seo.menu_visible === false`. Every consumer therefore has to apply the rule
 * itself, which `Category.jsx` already did inline; keeping it in one place is
 * what stops the menu and the category page disagreeing about what is hidden.
 *
 * There is no tree endpoint either, so the parent/child shape is rebuilt from
 * `parent_id` here rather than asking the backend for a second representation
 * of data it already sends.
 */

/**
 * Shelves an editor has hidden, plus the `logix-*` rows the retired wholesale
 * importer left behind.
 *
 * Nothing mints those handles any more — the importer is gone — but the rows
 * are still in `categories`, they are still the great majority of them, and
 * many still hold live products. This is therefore not dead code: delete the
 * prefix check and the header menu fills with hundreds of per-product scratch
 * shelves. It goes when the rows go, not before.
 */
export function isMenuVisibleCategory(category) {
  if (!category?.handle) return false;
  if (category.handle.startsWith("logix-")) return false;
  return category.seo?.menu_visible !== false;
}

/**
 * Flat rows -> departments with their garment types.
 *
 * API order (position, then handle) is preserved rather than re-sorted, so an
 * admin reordering the taxonomy takes effect without a deploy. A child whose
 * parent is hidden is promoted to the top level rather than disappearing with
 * it — losing a live shelf is worse than showing it a level up.
 */
export function buildCategoryTree(rows) {
  const list = (Array.isArray(rows) ? rows : []).filter(isMenuVisibleCategory);
  const byId = new Map(list.map((category) => [category.id, { ...category, children: [] }]));
  const roots = [];
  list.forEach((category) => {
    const node = byId.get(category.id);
    const parent = category.parent_id ? byId.get(category.parent_id) : null;
    (parent ? parent.children : roots).push(node);
  });
  return roots;
}

/** Always the plural form: `/category/:handle` only exists as a redirect. */
export function categoryPath(handle) {
  return `/categories/${handle}`;
}

/**
 * `?category=a,b` — kateqoriya səhifəsində bir neçə alt kateqoriyanın birgə
 * seçimi (sol filtrdəki KATEQORİYALAR checkbox-ları). Backend vergüllə
 * ayrılmış handle-ları alt-ağacları ilə birlikdə birləşdirir.
 */
export function parseCategoryParam(value) {
  const seen = new Set();
  String(value || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
    .forEach((handle) => seen.add(handle));
  return [...seen];
}

/**
 * Bu kökün hansı menyu bölməsinə aid olduğu — breadcrumb-ın ikinci pilləsi
 * («Ana səhifə / Geyim / Şalvarlar / Klassik»). Naviqasiya admin-dədir, ona
 * görə əvvəlcə menyunun özünə baxılır: kök bir bəndin öz ünvanıdırsa
 * (Ayaqqabı, Aksesuarlar) ayrıca bölmə pilləsi yoxdur; bir bəndin panelində
 * linkdirsə həmin bənd (Geyim → /shop) bölmədir. Menyu yüklənməyibsə,
 * köhnə qayda: öz bəndi olmayan hər geyim kökü «Geyim»-dədir.
 */
const OWN_SECTION_ROOTS = ["ayaqqabi", "aksesuar"];

export function findNavSection(navItems, rootHandle) {
  if (!rootHandle) return null;
  const path = categoryPath(rootHandle);
  const items = Array.isArray(navItems) ? navItems : [];
  if (items.some((item) => item.to === path)) return null;
  const owner = items.find((item) => (item.columns || []).some(
    (column) => column.to === path || (column.links || []).some((link) => link.to === path),
  ));
  if (owner) return owner.to ? { title: owner.title, to: owner.to } : null;
  if (OWN_SECTION_ROOTS.includes(rootHandle) || rootHandle === "yeni-gelenler") return null;
  return { title: { az: "Geyim", en: "Clothing" }, to: "/shop" };
}

export default buildCategoryTree;
